/**
 * @file ContextMenu.tsx
 *
 * Shared contextual (kebab) menu for per-row/card actions, used by the products
 * and orders lists. The parent computes the anchor position; this renders the
 * menu fixed near the trigger on desktop and as a bottom sheet on mobile, behind
 * a full-screen overlay that closes it on outside click. Each item carries an
 * icon and a localized label; the selected item's `value` is passed back.
 */

import { Icon } from '@group.one/react-gravity';
import type { IconName } from '@group.one/gravity';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import styles from './ContextMenu.module.css';

/** A single menu action: its value, icon, and localized label. */
export interface ContextMenuItem<T extends string> {
  value: T;
  iconName: IconName;
  label: MessageDescriptor;
}

interface MenuPosition {
  top?: number;
  left?: number;
}

interface ContextMenuProps<T extends string> {
  items: ContextMenuItem<T>[];
  /** Anchor position (desktop); ignored when `isMobile`. */
  position: MenuPosition;
  isMobile: boolean;
  /** `data-testid` namespace (e.g. `product`, `order`). */
  testIdPrefix: string;
  onSelect: (value: T) => void;
  onClose: () => void;
}

/**
 * Renders the contextual menu and its dismiss overlay.
 *
 * @param props - Items, anchor position, viewport flag, testid namespace, and
 *   the select/close handlers.
 *
 * @returns The menu element.
 */
export function ContextMenu<T extends string>({
  items,
  position,
  isMobile,
  testIdPrefix,
  onSelect,
  onClose,
}: ContextMenuProps<T>) {
  const intl = useIntl();

  return (
    <>
      <div
        className={styles.menuOverlay}
        data-testid="menu-overlay"
        onMouseDown={onClose}
      />
      <div
        data-testid={`${testIdPrefix}-menu`}
        style={
          isMobile
            ? { position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }
            : {
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 1000,
              }
        }
      >
        <div className={`gv-contextual-menu ${styles.menu}`}>
          <div className="gv-menu">
            <button
              type="button"
              className="gv-btn-close"
              aria-label={intl.formatMessage(adminMessages.productsClose)}
              onClick={onClose}
            >
              <Icon name="close" size="small" />
            </button>
            <ul>
              {items.map((item) => (
                <li key={item.value}>
                  <button
                    type="button"
                    className="gv-menu-item"
                    data-testid={`${testIdPrefix}-menu-${item.value}`}
                    onClick={() => onSelect(item.value)}
                  >
                    <Icon name={item.iconName} size="small" />
                    <span>{intl.formatMessage(item.label)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
